import fromPairs from 'lodash/fromPairs';
import keyBy from 'lodash/keyBy';
import isNil from 'lodash/isNil';
import pick from 'lodash/pick';
import toPairs from 'lodash/toPairs';
import values from 'lodash/values';
import {
  Price,
  Claim,
  ClaimSplit,
  PatientInvoice,
  PatientStatementRequest,
  NotSetSentinal,
  ChargesRowData,
  ChargeInfo,
  ChargesCodeInfo,
  ChargesRowCommonInfo,
  ExtendedServiceCode,
  ClaimRowData,
  ClaimAttachment,
} from 'pages/Dashboard/pages/Billing/types';
import {
  ChargesResponseDTO,
  ClaimBundleDTO,
  VisitNoteBillingCodeDTO,
  SplitClaimsDTO,
  EOBWithInsurancePaymentRequestDTO,
  VisitNoteBillingAssessmentCodeDTO,
  PatientStatementDTO,
  ChargeMasterDTO,
  CustomChargeMasterDTO,
  PatientStatementRequestDTO,
  EncounterPatientWriteOffDTO,
  NdcCodeDTO,
  ChargeResponseServiceCodeDTO,
  ChargeDetailsResponseDTO,
  ClaimDetailsResponseDTO,
  ClaimServiceCodeDefaultsDTO,
  PatientDTO,
  ClaimsResponseDTO,
  ClaimAttachmentDTO,
  EncounterPatientWriteOffRequestDTO,
} from 'dtos';
import {
  formatDateOnly,
} from 'utils/date';
import dayjs from 'utils/dayjs';
import {
  getDefaultIf,
  isEmptyString,
  setDefaultValueIf,
} from '@populateui/utils';
import {
  normalizePhoneNumber,
} from 'pages/Dashboard/utils/helper';
import pickBy from 'lodash/pickBy';
import {
  withServiceCodesAllocation,
} from 'pages/Dashboard/pages/Billing/Payments/utils';
import {
  ServiceCodeDetails,
  VisitNoteBillingCode,
} from 'views/EMR/components/ServiceLookup/type';
import map from 'lodash/map';
import {
  formatLinkedICDCodes,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/utils/helper';
import compact from 'lodash/compact';
import {
  NotifyProps,
} from 'core/hooks';
import head from 'lodash/head';
import groupBy from 'lodash/groupBy';

const paymentNotifications: readonly Pick<
  NotifyProps, 'variant' | 'message'>[] = Object.freeze([{
    variant: 'error',
    message: 'Payment Failed, please try again later',
  }, {
    variant: 'success',
    message: 'Payment Received Successfully',
  }, {
    variant: 'info',
    message: 'Payment Cancelled',
  }]);

const achFields: readonly string[] = Object.freeze([
  'senderAccountNumber',
  'senderRoutingNumber',
  'receiverAccountNumber',
  'receiverRoutingNumber',
]);

export const paymentNotificationsByVariant = Object.freeze(keyBy(paymentNotifications, 'variant'));

function isNotSet(code: string | null | undefined): boolean {
  return isEmptyString(code) || code === NotSetSentinal;
}

function formatNdcCode(ndc?: NdcCodeDTO | null | string) {
  return typeof ndc === 'string' ? {
    convertedNdcPackageCode: ndc,
  } : ndc;
}

export function normalizeBillingCodes(
  billingCodes: VisitNoteBillingCodeDTO[],
  serviceCodeDefaults?: ClaimServiceCodeDefaultsDTO,
  assessmentCodes?: VisitNoteBillingAssessmentCodeDTO[],
): VisitNoteBillingCode[] {
  return (billingCodes ?? []).map((billingCode) => ({
    ...billingCode,
    serviceCodeDefaults,
    serviceCodeDetails: {
      ...billingCode?.serviceCodeDetails,
      priorAuthCode:
        billingCode?.serviceCodeDetails?.priorAuthCode
        ?? serviceCodeDefaults?.priorAuthCode,
      pos:
        billingCode.serviceCodeDetails?.pos
        ?? (serviceCodeDefaults as ServiceCodeDetails)?.pos,
      serviceDate: dayjs(
        billingCode?.serviceCodeDetails?.serviceDate
        ?? serviceCodeDefaults?.serviceDate,
      ),
      drugIdentification: {
        ...(billingCode?.serviceCodeDetails?.drugIdentification ?? {}),
        unitCode: {
          code: billingCode?.serviceCodeDetails?.drugIdentification?.unitCode
            ?? (serviceCodeDefaults?.drugIdentification?.unitCode as any)?.code
            ?? '',
        },
      },
    },
    assessmentCodes: assessmentCodes?.map(({
      keyId, visitNoteBillingAsessmentCodeId, ...assessmentCodes
    }) => assessmentCodes),
  }));
}

export function getClaimWarningMessages(bundle?: ClaimBundleDTO): string[] {
  const claimWarnings = bundle?.claim?.details?.authorization?.warnings ?? [];
  const serviceWarnings = bundle?.visitNote?.bill?.serviceCodes?.flatMap(
    ({ claimServiceLine }) => claimServiceLine?.warnings ?? [],
  ) ?? [];

  return [...new Set([...claimWarnings, ...serviceWarnings].map((warning) => warning.message))];
}

// @TODO: consolidate format billing codes
export const serializeBillingCodesForClaim = (
  billingCodes: VisitNoteBillingCode[],
  assessmentCodes: VisitNoteBillingAssessmentCodeDTO[],
): VisitNoteBillingCodeDTO[] => {
  const orderIndexes = fromPairs(assessmentCodes.map(({ code }, index) => ([code, index])));
  const availableCodes = new Set(compact(map(assessmentCodes, 'code')));

  return billingCodes
    .map(({
      linkedICDCodes,
      serviceCodeDetails,
      modifiers,
      ...item
    }: VisitNoteBillingCode) => ({
      ...item,
      linkedICDCodes: formatLinkedICDCodes(
        linkedICDCodes ?? [],
        orderIndexes,
        availableCodes,
      ),
      serviceCodeDetails: {
        ...(serviceCodeDetails ?? {}),
        serviceDate: formatDateOnly(serviceCodeDetails?.serviceDate ?? ''),
        drugIdentification: {
          ...serviceCodeDetails?.drugIdentification,
          ndc: formatNdcCode(
            serviceCodeDetails?.drugIdentification?.ndc,
          )?.convertedNdcPackageCode,
          unitCount: Number(serviceCodeDetails?.drugIdentification?.unitCount) === 0
            ? null : Number(serviceCodeDetails?.drugIdentification?.unitCount),
          unitCode: serviceCodeDetails?.drugIdentification?.unitCode?.code ?? '',
        },
      },
      modifiers: (modifiers ?? [])
        .map((modifier) => ({
          ...modifier,
          description: modifier.description ?? modifier.code,
        })),
    }));
};

function normalizeServiceCodes(
  serviceCodes: ChargeResponseServiceCodeDTO[],
): ExtendedServiceCode[] {
  return serviceCodes.map(({ modifiers, icdCodes, ...serviceCode }) => ({
    ...serviceCode,
    modifiers: (modifiers ?? []).map((modifier) => ({ code: modifier })),
    linkedICDCodes: (icdCodes ?? []).map((linkedICDCode) => ({ code: linkedICDCode })),
  }));
}

export function normalizeClaims({ claims }: ClaimsResponseDTO): ClaimRowData[] {
  return (claims?.data ?? []).map(({
    claim,
    patient,
    visitDateTime,
    encounterId,
    reasonForVisit,
    isOutOfPocket,
    isFinalized,
    billingReviewState,
    hasDetails,
    billedAmount,
    allowedAmount,
    insurancePaidAmount,
  }) => {
    const commonInfo = {
      patientId: patient?.patientId,
      encounterId,
      isOutOfPocket,
      isChild: false,
      reasonForVisit: reasonForVisit ?? '',
      visitDateTime: visitDateTime ?? '',
      isFinalized,
      billingState: billingReviewState,
    };

    return {
      ...commonInfo,
      ...claim,
      billedAmount,
      allowedAmount,
      insurancePaidAmount,
      isClaim: true,
      patient,
      expandable: Boolean(hasDetails),
    };
  });
}

export function buildClaimsInfo(
  row: ClaimRowData,
  details?: ClaimDetailsResponseDTO | null,
): Partial<ClaimRowData>[] {
  if (isNil(details)) {
    return [];
  }

  const normalizedServiceCodes = normalizeServiceCodes(details.serviceCodes ?? []);
  const commonInfo = {
    patientId: row.patientId,
    encounterId: row.encounterId,
    isOutOfPocket: row.isOutOfPocket,
    isChild: true,
  };

  return normalizedServiceCodes.map((code) => ({
    ...commonInfo,
    ...code,
    isCode: true,
  }));
}

export function buildChargesInfo(
  row: ChargesRowData,
  details?: ChargeDetailsResponseDTO | null,
): Partial<ChargeInfo>[] {
  if (isNil(details)) {
    return row.chargesInfo ?? [];
  }

  const claimCharges = row.claimCharges ?? [];
  const result: Partial<ChargeInfo>[] = [];
  const normalizedServiceCodes = normalizeServiceCodes(details.serviceCodes ?? []);
  const serviceCodesByClaimIds = groupBy(
    normalizedServiceCodes,
    (serviceCode) => serviceCode?.primaryClaimId ?? 0,
  );
  const commonInfo: ChargesRowCommonInfo = {
    patientId: row.patientId,
    masterClaimId: row.masterClaimId,
    encounterId: row.encounterId,
    isOutOfPocket: row.isOutOfPocket,
    isChild: true,
  };

  claimCharges.forEach((claimCharge) => {
    result.push(claimCharge);

    const codesByClaim = serviceCodesByClaimIds[claimCharge.claimId ?? 0];
    codesByClaim?.forEach((code) => {
      result.push({
        ...commonInfo,
        ...code,
        isCode: true,
      } as ChargesCodeInfo);
    });
  });

  (details.customServiceCodes ?? []).forEach((code) => {
    result.push({
      ...commonInfo,
      ...code,
      isCode: true,
    } as ChargesCodeInfo);
  });

  return result;
}

export function getChargeSubRows(
  row: ChargesRowData,
  details?: ChargeDetailsResponseDTO,
  isFetching = false,
): Partial<ChargeInfo>[] | undefined {
  if (row.isChild) {
    return undefined;
  }
  if (!isNil(details)) {
    return buildChargesInfo(row, details);
  }
  if (row.expandable && isFetching) {
    return [
      ...(row.chargesInfo ?? []),
      {
        isChild: true,
        isDetailsLoading: true,
        rowKey: `details-loading-${row.encounterId ?? 0}`,
        encounterId: row.encounterId,
      },
    ];
  }
  return row.chargesInfo ?? [];
}

export function getClaimSubRows(
  row: ClaimRowData,
  details?: ClaimDetailsResponseDTO,
): Partial<ClaimRowData>[] | undefined {
  if (row.isChild) {
    return undefined;
  }
  if (!isNil(details)) {
    return buildClaimsInfo(row, details);
  }
  if (row.expandable) {
    return [{
      isChild: true,
      isDetailsLoading: true,
      rowKey: `details-loading-${row.claimId ?? 0}`,
      claimId: row.claimId,
    }];
  }
  return [];
}

export function normalizeCharges({ charges }: ChargesResponseDTO): ChargesRowData[] | undefined {
  return charges?.data?.map(({
    claims,
    patient,
    visitDateTime,
    encounterId,
    isFinalized,
    reasonForVisit,
    billingReviewState,
    patientResponsibility,
    patientBalance,
    isOutOfPocket,
    insuranceBalance,
    hasDetails,
    billedAmount,
    allowedAmount,
    insurancePaidAmount,
  }) => {
    const keyedSecondaryClaims = keyBy(
      (claims ?? []).filter(({ referencedPrimaryClaimId }) => !isNil(referencedPrimaryClaimId)),
      'referencedPrimaryClaimId',
    ) ?? {};

    const primaryClaimIds = (claims ?? []).filter(
      ({ referencedPrimaryClaimId }) => isNil(referencedPrimaryClaimId),
    ).map(({ claimId }) => claimId);

    const claimsById = keyBy(claims?.map((data) => ({ ...data, claimId: data.claimId ?? 0 })), 'claimId');
    const masterClaimId = head(claims)?.claimId ?? 0;

    const commonInfo: ChargesRowCommonInfo = {
      patientId: patient?.patientId,
      masterClaimId,
      encounterId,
      isOutOfPocket,
      isChild: true,
    };

    const claimCharges: ChargeInfo[] = primaryClaimIds.map((id, index) => ({
      ...commonInfo,
      ...claimsById[id ?? 0],
      secondaryClaim: keyedSecondaryClaims[id ?? 0],
      isClaim: true,
      claimIndex: primaryClaimIds?.length === 1 ? undefined : index,
    }));

    return {
      patient: patient as PatientDTO,
      chargesInfo: claimCharges,
      claimCharges,
      billingState: billingReviewState,
      keyedSecondaryClaims,
      visitDateTime,
      encounterId,
      patientId: patient?.patientId,
      isFinalized,
      reasonForVisit,
      patientResponsibility,
      patientBalance,
      isOutOfPocket,
      masterClaimId,
      billedAmount,
      allowedAmount,
      insurancePaidAmount,
      expandable: Boolean(hasDetails),
      insuranceBalance,
    };
  });
}

export function normalizeClaimForm({
  claim,
  insuranceAmountPaid,
  visitNote,
  serviceCodeDefaults,
}: ClaimBundleDTO): Claim {
  return {
    insuranceAmountPaid,
    visitNote,
    assessmentCodes: visitNote?.bill?.assessmentCodes ?? [],
    billingCodes: normalizeBillingCodes(
      visitNote?.bill?.serviceCodes ?? [],
      serviceCodeDefaults,
      visitNote?.bill?.assessmentCodes ?? [],
    ),
    ...pick(claim, ['claimId', 'patientId', 'encounterId', 'organizationId', 'externalId']),
    dates: (
      claim?.details?.dates ?? []
    ).map(({ qualifier, date }) => ({
      item: { qualifier: qualifier ?? '' },
      date: dayjs(date),
    })),
    patientAuthorizationSelection: isEmptyString(claim?.details?.authorization?.authorizationNumber)
      ? undefined
      : { authorizationNumber: claim?.details?.authorization?.authorizationNumber ?? null },
    ...pick(claim?.details ?? {}, [
      'autoAccident',
      'otherAccident',
      'originalReferenceNumber',
      'additionalInformation',
    ]),
    ...(claim?.context ?? {}),
    ...(claim?.details?.employment ?? {}),
    notes: claim?.notes ?? '',
    referencedPrimaryPayerSecondaryIdentifier: claim?.referencedPrimaryPayerSecondaryIdentifier ?? '',
    billingProvider: { billingProviderId: claim?.context?.billingProviderId ?? 0 },
    renderingProvider: { userId: claim?.context?.renderingProviderUserId ?? 0 },
    serviceFacility: { serviceFacilityId: claim?.context?.serviceFacilityId ?? 0 },
    insurance: { insuranceId: claim?.insuranceDetails?.insuranceId ?? 0 },
    benefitsAssignment: { code: claim?.insuranceDetails?.benefitsAssignment ?? NotSetSentinal },
    planParticipation: { code: claim?.insuranceDetails?.planParticipation ?? NotSetSentinal },
    insurancePolicy: { code: claim?.insuranceDetails?.insurancePolicy ?? NotSetSentinal },
    insuranceType: { code: claim?.insuranceDetails?.insuranceType ?? NotSetSentinal },
    delayReasonCode: { code: claim?.details?.delayReasonCode ?? NotSetSentinal },
    resubmissionCode: { code: claim?.details?.resubmissionCode },
    referencedPrimaryClaimId: claim?.referencedPrimaryClaimId,
    claimAttachments: (claim?.claimAttachments ?? []).map((attachment) => ({
      ...attachment,
      transmissionCode: { code: attachment.attachmentTransmissionCode },
    })),
    updatedOn: claim?.updatedOn ?? '',
  };
}

function serializeClaimAttachments(claimAttachments: ClaimAttachment[]): ClaimAttachmentDTO[] {
  return (claimAttachments ?? []).map(({
    transmissionCode,
    patientMultiMediaId,
    attachmentControlNumber,
    ...attachment
  }) => ({
    ...attachment,
    attachmentTransmissionCode: transmissionCode?.code,
    ...(transmissionCode?.isFileUploadSupported ? {
      patientMultiMediaId: patientMultiMediaId ?? undefined,
    } : {
      attachmentControlNumber: attachmentControlNumber ?? undefined,
    }),
  }));
}

export function serializeClaimForm({
  serviceFacility,
  referringProvider,
  supervisingProvider,
  benefitsAssignment,
  planParticipation,
  insurance,
  insuranceType,
  insurancePolicy,
  billingProvider,
  renderingProvider,
  additionalInformation,
  dates,
  patientAuthorizationSelection,
  isRelatedToEmployment,
  autoAccident,
  otherAccident,
  delayReasonCode,
  resubmissionCode,
  originalReferenceNumber,
  assessmentCodes,
  billingCodes,
  notes,
  referencedPrimaryPayerSecondaryIdentifier,
  claimAttachments,
  ...formData
}: Claim, originalData?: ClaimBundleDTO): ClaimBundleDTO {
  return {
    visitNote: {
      ...(originalData?.visitNote ?? {}),
      version: formData?.visitNote?.version ?? originalData?.visitNote?.version ?? undefined,
      bill: {
        ...(originalData?.visitNote?.bill ?? {}),
        assessmentCodes,
        serviceCodes: serializeBillingCodesForClaim(billingCodes ?? [], assessmentCodes ?? []),
      },
    },
    claim: {
      ...(originalData?.claim ?? {}),
      context: {
        referringProvider: values(referringProvider).every(isEmptyString) ? undefined : {
          ...referringProvider,
          ...setDefaultValueIf(referringProvider ?? {}, null, isEmptyString),
        },
        supervisingProvider: values(supervisingProvider).every(isEmptyString) ? undefined : {
          ...supervisingProvider,
          ...setDefaultValueIf(supervisingProvider ?? {}, null, isEmptyString),
        },
        billingProviderId: billingProvider?.billingProviderId === 0
          ? undefined : billingProvider?.billingProviderId,
        renderingProviderUserId: renderingProvider?.userId,
        serviceFacilityId: serviceFacility?.serviceFacilityId === 0
          ? undefined : serviceFacility?.serviceFacilityId,
      },
      details: {
        employment: {
          isRelatedToEmployment: isRelatedToEmployment ?? false,
        },
        autoAccident: {
          isAutoAccident: autoAccident?.isAutoAccident ?? false,
          autoAccidentState: autoAccident?.autoAccidentState,
        },
        otherAccident: {
          isOtherAccident: otherAccident?.isOtherAccident ?? false,
        },
        additionalInformation,
        dates: dates?.map(({ item, date }) => ({
          qualifier: item?.qualifier ?? '',
          date: formatDateOnly(date),
        })),
        authorization: {
          authorizationNumber: patientAuthorizationSelection?.authorizationNumber ?? null,
        },
        delayReasonCode: isNotSet(delayReasonCode?.code)
          ? undefined : delayReasonCode?.code,
        resubmissionCode: isEmptyString(resubmissionCode?.code)
          ? undefined : resubmissionCode?.code,
        originalReferenceNumber,
      },
      insuranceDetails: {
        benefitsAssignment: isNotSet(benefitsAssignment?.code)
          ? undefined : benefitsAssignment?.code,
        planParticipation: isNotSet(planParticipation?.code) ? undefined : planParticipation?.code,
        insuranceId: insurance?.insuranceId === 0 ? null : insurance?.insuranceId,
        insuranceType: isNotSet(insuranceType?.code) ? undefined : insuranceType?.code,
        insurancePolicy: isNotSet(insurancePolicy?.code) ? undefined : insurancePolicy?.code,
      },
      claimAttachments: serializeClaimAttachments(claimAttachments ?? []),
      notes,
      referencedPrimaryPayerSecondaryIdentifier,
    },
  };
}

export function normalizeClaimSplits(claim: SplitClaimsDTO): ClaimSplit[] {
  return (claim?.splitClaims ?? []).map((claimSplit) => ({
    ...claimSplit,
    claimId: (claimSplit?.claimId ?? 0).toString(),
    serviceCodes: (claimSplit.serviceCodes ?? []).map((serviceCode) => ({
      ...serviceCode,
      serviceCodeDetails: {
        ...(serviceCode?.serviceCodeDetails ?? {}),
        primaryClaimId: (claimSplit?.claimId ?? 0).toString(),
      },
    })),
  }));
}

function serializeServiceCodesForSplit(
  serviceCodes: VisitNoteBillingCodeDTO[],
  claimId: number,
): VisitNoteBillingCodeDTO[] {
  return (serviceCodes ?? []).map((service) => ({
    ...service,
    serviceCodeDetails: {
      ...service?.serviceCodeDetails,
      primaryClaimId: claimId,
    },
  }));
}

export function serializeClaimSplits(splits: ClaimSplit[]): SplitClaimsDTO {
  return {
    splitClaims: splits.map((claimSplit) => {
      const claimId = /^new-/ig.test(claimSplit.claimId) ? 0 : Number(claimSplit.claimId);
      return {
        ...claimSplit,
        insuranceId: claimSplit?.insuranceId === 0 ? null : claimSplit?.insuranceId,
        externalId: claimSplit?.externalId ?? '',
        status: claimSplit?.status ?? 'Draft',
        claimId,
        serviceCodes: serializeServiceCodesForSplit(
          (claimSplit?.serviceCodes ?? []) as VisitNoteBillingCodeDTO[],
          claimId,
        ),
      };
    }),
  };
}

export function serializeEOBWithInsurancePaymentRequest(
  data: EOBWithInsurancePaymentRequestDTO,
): EOBWithInsurancePaymentRequestDTO {
  const { discriminator } = data?.insurancePayment?.paymentMethod ?? {};

  const isACHPayment = /^ach/i.test(discriminator ?? '');
  const achFieldValues = pick(data?.insurancePayment?.paymentMethod, achFields);

  const achInfo = isACHPayment ? {
    ...achFieldValues,
    ...getDefaultIf(achFieldValues, null, isEmptyString),
  } : {};

  return {
    ...data,
    ...(!isNil(data?.insurancePayment) && {
      insurancePayment: {
        ...data.insurancePayment,
        paymentMethod: {
          ...data.insurancePayment?.paymentMethod,
          ...setDefaultValueIf(achInfo, null, isEmptyString),
        },
      },
    }),
  };
}

export const normalizePatientInvoice = (data: PatientStatementDTO): PatientInvoice => ({
  ...data,
  visitNotesForStatement: toPairs(data.billingData ?? {}).reduce((acc, [key, value]) => {
    const { billingCodes, customServiceCodes, encounterId, isOutOfPocket } = value;
    const allocation = withServiceCodesAllocation(
      [...(billingCodes ?? []), ...(customServiceCodes ?? [])],
      { encounterId: encounterId ?? 0 },
      isOutOfPocket,
      true,
    );
    return {
      ...acc,
      [key]: {
        ...value,
        patientBalance: data?.patientEncounterFinancial?.[key] ?? {},
        allocation: allocation?.map((item) => (
          {
            ...item,
            comment: (data?.billingCodeComments ?? {})[item.visitNoteBillingCodeId as number],
          })),
      },
    };
  }, {}),
});

export function normalizeCPTPrice({
  chargeMasterId: id,
  billingCodeId: codeId,
  ...data
}: ChargeMasterDTO) {
  return {
    id,
    codeId,
    isCustom: false,
    ...data,
  };
}

export function normalizeCustomServicePrice({
  customServiceCodeId: codeId,
  customChargeMasterId: id,
  ...data
}: CustomChargeMasterDTO) {
  return {
    id,
    codeId,
    isCustom: true,
    ...data,
  };
}

export function serializePrice({
  id,
  codeId,
  isCustom,
  chargeAmount,
  billingCode,
  customServiceCode,
}: Price) {
  return {
    ...(isCustom
      ? {
        customChargeMasterId: id,
        customServiceCodeId: codeId ?? customServiceCode?.customServiceCodeId,
        customServiceCode: {
          ...customServiceCode,
          serviceName: customServiceCode?.serviceName?.trim(),
        },
      }
      : {
        chargeMasterId: id,
        billingCodeId: codeId ?? billingCode?.billingCodeId,
        billingCode,
      }
    ),
    chargeAmount,
  };
}

export function serializePatientStatementPayload(
  data: PatientStatementRequest,
): PatientStatementRequestDTO {
  const { phone, billInfo = [], ...payload } = data;

  return {
    ...pickBy(payload, (value) => !isEmptyString(value)),
    ...(isEmptyString(phone)) ? {} : { phone: normalizePhoneNumber(phone ?? '') },
    ...(isNil(billInfo) ? {} : {
      paymentByEncounters: billInfo.map(
        ({ encounterId, balance }) => ({ encounterId, paymentAmount: balance }),
      ),
    }),

  };
}

export function serializeWriteOffs(
  data: EncounterPatientWriteOffDTO[],
  encounterId: number | string,
): EncounterPatientWriteOffRequestDTO {
  return {
    encounterPatientWriteOffs: {
      [encounterId]: data,
    },
  };
}
